/**
 * OutreachTimeline (KAN-660)
 *
 * Day bar 00:00-24:00 of the "Рассылка сегодня" block: the sending window
 * (highlighted, labelled with the plan), the filled part from the window
 * start to now, one tick per sent first message, and the "now" line.
 * Geometry comes from `lib/outreachTimeline` (positions in percent in the
 * window time zone); this file only renders it. KAN-664: every hour of the bar
 * is a hover/focus zone with a tooltip (`OutreachHourZones`): who got a message
 * in that hour and who is planned for it. KAN-679: the hovered/focused hour is
 * highlighted on the bar (blue fill + inset ring, under the sent ticks and the
 * red "now" line) and captioned under the bar ("13:00-14:00").
 */
import { useState } from 'react'
import type { CSSProperties } from 'react'
import type { OutreachPlannedRecipient, OutreachSentMark, OutreachWindow } from '@/services/api/dashboard'
import {
  HOUR_LABELS,
  buildDayTimeline,
  hourBuckets,
  hourRangeLabel,
  hourToPercent,
  zoneLabel,
} from '@/lib/outreachTimeline'
import { OutreachHourZones } from './OutreachHourZones'
import { formatWeekdays } from '@/lib/weekdays'

const HOUR_TICKS = Array.from({ length: 25 }, (_, hour) => hour)
const HOUR_WIDTH_PERCENT = 100 / 24

/**
 * Position of the hovered-hour caption under the bar: centred on the hour,
 * pinned to the edge for the first/last hours so it does not leave the row.
 */
function captionStyle(hour: number): CSSProperties {
  if (hour < 2) return { left: 0 }
  if (hour > 21) return { right: 0 }
  return { left: `${hourToPercent(hour) + HOUR_WIDTH_PERCENT / 2}%`, transform: 'translateX(-50%)' }
}

interface Props {
  window: OutreachWindow | null
  sentTimes: OutreachSentMark[]
  /** KAN-664: next recipients in planner order (planned block of the hour tooltip). */
  planned?: OutreachPlannedRecipient[]
  /** Accounts limit for the day (sum of account limits). */
  capacity: number
  /** Sent today (progress.sent_today). */
  sent: number
  now: Date
}

/** KAN-764: "пн-пт " prefix when the backend sends window.days. */
function windowDays(window: OutreachWindow): string {
  const days = window.days ? formatWeekdays(window.days) : ''
  return days ? `${days} ` : ''
}

export function OutreachTimeline({ window, sentTimes, planned = [], capacity, sent, now }: Props) {
  const [activeHour, setActiveHour] = useState<number | null>(null)
  const timeline = buildDayTimeline({ window, sentTimes, now })
  const buckets = hourBuckets(sentTimes, planned, window, now)
  const windowLabel = window
    ? `окно ${windowDays(window)}${window.start}-${window.end}, лимит аккаунтов ${capacity}`
    : 'Окно не настроено'
  const windowLeft = timeline.window?.startPercent ?? 0
  const labelOnRight = windowLeft > 60

  return (
    <div data-testid="outreach-timeline" className="mt-3">
      <div className="relative h-5 text-xs">
        <span
          data-testid="outreach-window-label"
          className="absolute top-0 whitespace-nowrap text-gray-600"
          style={labelOnRight ? { right: 0 } : { left: `${windowLeft}%` }}
        >
          {windowLabel}
        </span>
        <span
          data-testid="outreach-now-label"
          className={`absolute right-0 top-0 whitespace-nowrap ${
            timeline.nowInWindow ? 'text-rose-600' : 'text-gray-400'
          } ${labelOnRight ? 'hidden' : ''}`}
        >
          сейчас {timeline.nowTime} {zoneLabel(timeline.timezone)}
        </span>
      </div>
      <div className="relative h-8 w-full overflow-hidden rounded bg-gray-100">
        {timeline.window && (
          <div
            data-testid="outreach-window-segment"
            className="absolute inset-y-0 border-x border-amber-300 bg-amber-50"
            style={{
              left: `${timeline.window.startPercent}%`,
              width: `${timeline.window.endPercent - timeline.window.startPercent}%`,
            }}
          />
        )}
        {timeline.fill && (
          <div
            data-testid="outreach-fill"
            className="absolute inset-y-0 bg-blue-200/70"
            style={{
              left: `${timeline.fill.fromPercent}%`,
              width: `${timeline.fill.toPercent - timeline.fill.fromPercent}%`,
            }}
          />
        )}
        {activeHour !== null && (
          <div
            data-testid="outreach-hour-highlight"
            aria-hidden
            className="absolute inset-y-0 z-[1] bg-blue-500/15 ring-1 ring-inset ring-blue-500/60"
            style={{ left: `${hourToPercent(activeHour)}%`, width: `${HOUR_WIDTH_PERCENT}%` }}
          />
        )}
        {HOUR_TICKS.map((hour) => (
          <span
            key={hour}
            aria-hidden
            className={`absolute bottom-0 z-[2] w-px bg-gray-300 ${hour % 6 === 0 ? 'h-2.5' : 'h-1.5'}`}
            style={{ left: `${hourToPercent(hour)}%` }}
          />
        ))}
        {timeline.marks.map((mark, index) => (
          <span
            key={`${mark.at}:${index}`}
            data-testid="outreach-sent-mark"
            title={`${mark.time} ${zoneLabel(timeline.timezone)}, ${mark.label}`}
            className="absolute bottom-1.5 top-4 z-[2] w-0.5 bg-blue-700"
            style={{ left: `${mark.percent}%` }}
          />
        ))}
        {timeline.fill && sent > 0 && (
          <span
            data-testid="outreach-sent-label"
            className="absolute top-0 z-[2] whitespace-nowrap px-1 text-[10px] leading-4 text-blue-800"
            style={
              timeline.fill.toPercent > 50
                ? { right: `${100 - timeline.fill.toPercent}%` }
                : { left: `${timeline.fill.toPercent}%` }
            }
          >
            отправлено {sent}
          </span>
        )}
        <span
          data-testid="outreach-now"
          title={`сейчас ${timeline.nowTime} ${zoneLabel(timeline.timezone)}`}
          className="absolute inset-y-0 z-[3] w-0.5 bg-rose-500"
          style={{ left: `${timeline.nowPercent}%` }}
        />
        <OutreachHourZones buckets={buckets} activeHour={activeHour} onActiveHourChange={setActiveHour} />
      </div>
      <div className="relative h-4 text-[10px] text-gray-400">
        {HOUR_LABELS.map((hour) => (
          <span
            key={hour}
            className="absolute top-0"
            style={{
              left: `${hourToPercent(hour)}%`,
              transform: hour === 0 ? 'none' : hour === 24 ? 'translateX(-100%)' : 'translateX(-50%)',
            }}
          >
            {String(hour).padStart(2, '0')}
          </span>
        ))}
        {activeHour !== null && (
          <span
            data-testid="outreach-hour-caption"
            className="absolute top-0 whitespace-nowrap rounded bg-white px-0.5 font-medium text-blue-700"
            style={captionStyle(activeHour)}
          >
            {hourRangeLabel(activeHour)}
          </span>
        )}
      </div>
    </div>
  )
}
